import {
  getUnsyncedEntries,
  deleteUnsyncedEntry,
  cacheEntry
} from './db';

console.log('✅ SyncManager loaded');

class SyncManager {
  constructor() {
    this.isSyncing = false;
    this.syncListeners = [];
  }

  // Register a listener for sync events
  addSyncListener(callback) {
    this.syncListeners.push(callback);
  }

  // Remove a listener
  removeSyncListener(callback) {
    this.syncListeners = this.syncListeners.filter(cb => cb !== callback);
  }

  // Notify all listeners
  notifySyncListeners(status) {
    this.syncListeners.forEach(callback => callback(status));
  }

  // Check if online
  isOnline() {
    return navigator.onLine;
  }

// Sync all unsynced entries
async syncAll() {
  console.log('🔄 SYNC STARTED');
  console.log('📱 Is syncing?', this.isSyncing);
  console.log('🌐 Is online?', this.isOnline());
  
  // If already syncing, don't start another sync
  if (this.isSyncing) {
    console.log('⚠️ Sync already in progress, skipping');
    return { success: false, message: 'Sync already in progress' };
  }
  
  if (!this.isOnline()) {
    console.log('❌ Cannot sync: offline');
    return { success: false, message: 'Cannot sync while offline' };
  }

  this.isSyncing = true;
  
  if (!this.isOnline()) {
    console.log('❌ Cannot sync: offline');
    return { success: false, message: 'Cannot sync while offline' };
  }
    this.notifySyncListeners({ syncing: true, progress: 0 });

    try {
      const unsyncedEntries = await getUnsyncedEntries();
      
      if (unsyncedEntries.length === 0) {
        console.log('No entries to sync');
        this.isSyncing = false;
        this.notifySyncListeners({ syncing: false, complete: true });
        return { success: true, count: 0, message: 'No entries to sync' };
      }

      console.log(`Syncing ${unsyncedEntries.length} entries...`);

      const results = {
        success: 0,
        failed: 0,
        errors: []
      };

      for (let i = 0; i < unsyncedEntries.length; i++) {
        const entry = unsyncedEntries[i];
        const progress = ((i + 1) / unsyncedEntries.length) * 100;
        
        this.notifySyncListeners({ 
          syncing: true, 
          progress,
          current: i + 1,
          total: unsyncedEntries.length
        });

        try {
          // Remove tempId before sending to server
          const { tempId, createdAt, ...entryData } = entry;

          // Send to server
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/hikes/${entry.hikeId}/entries`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: JSON.stringify(entryData)
            }
          );

          if (!response.ok) {
            throw new Error(`Server responded with ${response.status}`);
          }

          const savedEntry = await response.json();

          // Cache the synced entry
          await cacheEntry(savedEntry);

          // Delete from unsynced store
          await deleteUnsyncedEntry(tempId);

          results.success++;
          console.log(`Synced entry ${i + 1}/${unsyncedEntries.length}`);

        } catch (error) {
          console.error(`Failed to sync entry ${i + 1}:`, error);
          results.failed++;
          results.errors.push({
            entry: entry.tempId,
            error: error.message
          });
        }
      }

      this.isSyncing = false;

      const allSynced = results.failed === 0;
      this.notifySyncListeners({ 
        syncing: false, 
        complete: allSynced,
        results 
      });

      if (allSynced) {
        return { 
          success: true, 
          count: results.success, 
          message: `Successfully synced ${results.success} entries` 
        };
      } else {
        return {
          success: false,
          count: results.success,
          failed: results.failed,
          message: `Synced ${results.success} entries, ${results.failed} failed`,
          errors: results.errors
        };
      }

    } catch (error) {
      console.error('Sync error:', error);
      this.isSyncing = false;
      this.notifySyncListeners({ syncing: false, error: error.message });
      return { success: false, message: error.message };
    }
  }

  // Attempt to sync when connection is restored
  setupAutoSync() {
    window.addEventListener('online', async () => {
      console.log('Connection restored, attempting auto-sync...');
      const result = await this.syncAll();
      if (result.success) {
        console.log('Auto-sync successful');
      } else {
        console.error('Auto-sync failed:', result.message);
      }
    });

    window.addEventListener('offline', () => {
      console.log('Connection lost, entering offline mode');
      this.notifySyncListeners({ offline: true });
    });

    // Check for unsynced data on page load
    setTimeout(async () => {
      if (this.isOnline()) {
        const unsyncedEntries = await getUnsyncedEntries();
        if (unsyncedEntries.length > 0) {
          console.log(`Found ${unsyncedEntries.length} unsynced entries on startup`);
          this.notifySyncListeners({ 
            hasUnsynced: true, 
            count: unsyncedEntries.length 
          });
        }
      }
    }, 1000);
  }
}

// Create singleton instance
const syncManager = new SyncManager();

// Auto-setup when module loads
syncManager.setupAutoSync();

export default syncManager;
